import type { FontRef, ID } from '@proofroom/schema';

export interface FaceLoadResult { loaded: ID[]; failed: ID[]; skipped: ID[] }

/** A face that has not arrived in this long is treated as failed: the viewer must not stay blocked on it. */
export const FACE_TIMEOUT_MS = 20_000;

interface Registration { href: string; face?: FontFace; promise: Promise<boolean> }

const loaded = new Set<ID>();
const registered = new Map<ID, Registration>();

export const faceLoaded = (id: ID): boolean => loaded.has(id);
export const loadedFaces = (): Set<ID> => new Set(loaded);
export function resetFaces(): void { loaded.clear(); registered.clear(); }

function woff2Of(f: FontRef): string | undefined {
  if (f.woff2Url) return f.woff2Url;
  if (f.url) return f.url.replace(/\.(ttf|otf)$/i, '.woff2');
  return undefined;
}

/**
 * Register a face under its PostScript name so `font-family: <fontId>` resolves to exactly
 * that file. Resolves true when the browser has it, false when it could not be loaded. Ingested
 * faces are versioned (`<ps>-<sha8>.woff2`), so a later draft can serve a new href for the same
 * id: when that happens, drop the stale FontFace from the set and register the new one.
 */
function registerFace(f: FontRef, set: FontFaceSet | undefined, timeoutMs: number): Promise<boolean> {
  const href = woff2Of(f) ?? '';
  const existing = registered.get(f.id);
  if (existing && existing.href === href) return existing.promise;
  if (existing) {
    if (existing.face && set) set.delete(existing.face);
    loaded.delete(f.id);
  }
  if (typeof FontFace === 'undefined' || !set || !href) {
    const p = Promise.resolve(false);
    registered.set(f.id, { href, promise: p });
    return p;
  }
  const face = new FontFace(f.postscriptName || f.id, `url(${href}) format("woff2")`, { display: 'block' });
  set.add(face);
  // A face that never settles used to leave the viewer waiting for ever, which blocks every frame click
  let timedOut = false;
  let timer: ReturnType<typeof setTimeout>;
  const expiry = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => {
      timedOut = true;
      console.warn(`[fonts] timed out loading ${f.id}`);
      resolve(false);
    }, timeoutMs);
  });
  const attempt = face.load().then(() => {
    if (!timedOut) loaded.add(f.id);
    return !timedOut;
  }).catch((err: unknown) => {
    console.warn(`[fonts] failed to load ${f.id} from ${href}`, err);
    return false;
  });
  const p = Promise.race([attempt, expiry]).finally(() => clearTimeout(timer));
  registered.set(f.id, { href, face, promise: p });
  return p;
}

/** Register every face the draft declares as available. Faces marked missing are skipped and reported, never guessed. */
export async function registerSnapshotFaces(
  fonts: FontRef[],
  scope: { fonts?: FontFaceSet } = globalThis.document ?? {},
  opts: { timeoutMs?: number } = {},
): Promise<FaceLoadResult> {
  const set = scope.fonts ?? (typeof self !== 'undefined' ? (self as unknown as { fonts?: FontFaceSet }).fonts : undefined);
  const timeoutMs = opts.timeoutMs ?? FACE_TIMEOUT_MS;
  const result: FaceLoadResult = { loaded: [], failed: [], skipped: [] };
  await Promise.all(fonts.map(async (f) => {
    if (f.status === 'missing' || !woff2Of(f)) { result.skipped.push(f.id); return; }
    (await registerFace(f, set, timeoutMs) ? result.loaded : result.failed).push(f.id);
  }));
  return result;
}
